'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@govinity/ui/components/ui/card';
import { MeetingStatus, resolveMeetingConfig } from '@govinity/validation';
import type { MeetingEventPublicGetResponse } from '@govinity/validation/public';
import { MessageSquare } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { useAuthStore } from '@/stores/authStore';
import { CommentList } from './comment-list';
import { useUserComments } from './use-user-comments';
import { useGovernmentContext } from '../../../context';

type CommentStatusCardProps = {
  meeting: Pick<MeetingEventPublicGetResponse, 'id' | 'status'>;
};

/**
 * Standing record on the meeting page of the comments the citizen has already submitted, so they
 * can review or withdraw them without opening the comment dialog.
 */
export function CommentStatusCard({ meeting }: CommentStatusCardProps) {
  const { government } = useGovernmentContext();
  const user = useAuthStore((state) => state.user);
  const t = useTranslations('PortalPublicComments');

  const { publicComments } = resolveMeetingConfig(government.meetingConfig);
  const isCommentPossible =
    publicComments.enabled && meeting.status === MeetingStatus.Upcoming && !!user;

  const { data: comments } = useUserComments(government.code, meeting.id, isCommentPossible);

  if (!comments?.records.length) {
    return null;
  }

  return (
    <Card className="bg-info-light mb-4 border-l-4">
      <CardHeader>
        <CardTitle className="text-info-foreground flex items-center gap-2 text-base">
          <MessageSquare aria-hidden="true" className="size-4" />
          {t('statusCardTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <CommentList records={comments.records} />
      </CardContent>
    </Card>
  );
}
